class NokshaLiveArchive {
  constructor(root) {
    this.root = root;
    this.layout = root.dataset.layout || 'page';
    this.source = root.dataset.source;
    this.locale = root.dataset.locale || 'en-GB';
    this.facebookUrl = root.dataset.facebookUrl || 'https://www.facebook.com/noksha101';
    this.grid = root.querySelector('[data-live-archive-grid]');
    this.loading = root.querySelector('[data-live-archive-loading]');
    this.empty = root.querySelector('[data-live-archive-empty]');
  }

  async init() {
    if (!this.source || !this.grid) return;

    try {
      const response = await fetch(this.source, { cache: 'no-store' });
      if (!response.ok) throw new Error(`Failed to load live videos (${response.status})`);
      const payload = await response.json();
      const videos = Array.isArray(payload.videos) ? payload.videos : [];

      this.hideLoading();

      if (!videos.length) {
        this.showEmpty();
        return;
      }

      this.render(videos);
      this.loadFacebookSdk();
    } catch (error) {
      this.hideLoading();
      this.showEmpty(error.message);
    }
  }

  hideLoading() {
    if (this.loading) this.loading.hidden = true;
  }

  showEmpty(message) {
    if (this.grid) this.grid.hidden = true;
    if (this.empty) {
      this.empty.hidden = false;
      if (message) {
        const note = this.empty.querySelector('[data-live-archive-error]');
        if (note) {
          note.textContent = message;
          note.hidden = false;
        }
      }
    }
  }

  formatDate(value, label) {
    if (label) return label;
    if (!value) return '';
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat(this.locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  }

  formatViews(views) {
    if (!views) return '';
    if (typeof views === 'number') {
      return new Intl.NumberFormat(this.locale, { notation: 'compact' }).format(views);
    }
    return String(views);
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;');
  }

  render(videos) {
    this.grid.innerHTML = videos
      .map((video) => {
        const dateLabel = this.formatDate(video.live_date, video.posted_label);
        const views = this.formatViews(video.views);
        const title = video.title || 'Facebook live video';

        return `
          <article class="noksha-live-archive__card">
            <div class="noksha-live-archive__card-media">
              <div class="fb-video" data-href="${this.escapeHtml(video.video_url)}" data-width="auto" data-show-text="false" data-allowfullscreen="true"></div>
            </div>
            <div class="noksha-live-archive__card-body">
              <div class="noksha-live-archive__meta">
                ${dateLabel ? `<time class="noksha-live-archive__date" datetime="${this.escapeHtml(video.live_date || '')}">${this.escapeHtml(dateLabel)}</time>` : ''}
                <span class="noksha-live-archive__platform noksha-live-archive__platform--facebook">Facebook Live</span>
                ${views ? `<span class="noksha-live-archive__views">${this.escapeHtml(views)} views</span>` : ''}
              </div>
              <h3 class="noksha-live-archive__card-title">${this.escapeHtml(title)}</h3>
              ${video.description ? `<p class="noksha-live-archive__card-description">${this.escapeHtml(video.description)}</p>` : ''}
              <a class="noksha-live-archive__watch-link" href="${this.escapeHtml(video.video_url)}" target="_blank" rel="noopener noreferrer">
                Watch on Facebook
              </a>
            </div>
          </article>
        `;
      })
      .join('');

    this.grid.hidden = false;
    if (this.empty) this.empty.hidden = true;
  }

  loadFacebookSdk() {
    if (document.getElementById('facebook-jssdk')) {
      if (window.FB) window.FB.XFBML.parse(this.root);
      return;
    }

    if (!document.getElementById('fb-root')) {
      const element = document.createElement('div');
      element.id = 'fb-root';
      document.body.appendChild(element);
    }

    const script = document.createElement('script');
    script.id = 'facebook-jssdk';
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.src = 'https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v21.0';
    script.onload = () => {
      if (window.FB) window.FB.XFBML.parse(this.root);
    };
    document.body.appendChild(script);
  }
}

document.querySelectorAll('[data-live-archive]').forEach((root) => {
  new NokshaLiveArchive(root).init();
});
